/**
 * Estadísticas de moderación del Centro de Monitoreo: día por día, por qué se escala (cada motivo
 * se abre y muestra sus mensajes) y en qué publicaciones se conversa, por tema y por marca (cada
 * publicación se abre y muestra sus comentarios). Los datos salen de lib/moderation-stats.
 */
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useModerationStats, usePostStats } from "@/hooks/use-moderation-stats";
import type { PostStat, StatsCounts, StatsMessage } from "@/hooks/use-moderation-stats";
import { formatArrivalAge } from "@/lib/format";
import { cn } from "@/lib/cn";

export const REASON_LABEL: Record<string, string> = {
  needs_context: "Necesita contexto",
  out_of_scope: "Fuera de lo que sabe el bot",
  complaint: "Queja o tono fuerte",
  personal_data: "Datos personales",
  legal: "Legal",
  medical: "Médico",
  language: "Otro idioma",
  send_error: "La red no aceptó la respuesta",
  handoff: "Derivado (KCC)",
  other: "Otros",
};

const COLOR = {
  answered: "var(--state-bot-text)",
  waiting: "var(--state-escalated-text)",
  notNeeded: "var(--state-closed-border)",
};

const answered = (c: StatsCounts) => c.answeredByBot + c.answeredByHuman;

function Split({ c }: { c: StatsCounts }) {
  const total = Math.max(c.received, 1);
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-surface-2" aria-hidden>
      <span style={{ width: `${(answered(c) * 100) / total}%`, background: COLOR.answered }} />
      <span style={{ width: `${(c.waiting * 100) / total}%`, background: COLOR.waiting }} />
      <span style={{ width: `${(c.notNeeded * 100) / total}%`, background: COLOR.notNeeded }} />
    </div>
  );
}

function Card({ title, subtitle, children, right }: { title: string; subtitle?: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-default bg-surface-1 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="font-sans text-sm font-semibold text-text-primary">{title}</h2>
          {subtitle && <p className="font-sans text-xs text-text-muted mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Chip({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-sans font-medium transition-colors",
        active
          ? "border-magenta bg-magenta-soft-bg text-text-primary"
          : "border-default bg-surface-2 text-text-secondary hover:text-text-primary",
      )}
    >
      {label}
      <span className="font-mono text-2xs tabular-nums text-text-muted">{count}</span>
    </button>
  );
}

function MessageRow({ m, clientSlug }: { m: StatsMessage; clientSlug: string }) {
  const inner = (
    <div className="px-4 py-2.5 hover:bg-surface-2 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span className="font-sans text-sm font-medium text-text-primary truncate">@{m.username ?? "anon"}</span>
        <span className="flex items-center gap-2 flex-shrink-0 font-sans text-2xs text-text-muted">
          {m.platform} · {m.kind === "comment" ? "comentario" : "dm"}
          <span className="font-mono tabular-nums">{formatArrivalAge(m.receivedAt)}</span>
        </span>
      </div>
      <p className="mt-0.5 font-sans text-sm text-text-secondary line-clamp-2">
        {m.body || <span className="text-text-muted">({m.attachment ?? "sin texto"})</span>}
      </p>
      {m.response && <p className="mt-0.5 font-sans text-sm text-text-muted line-clamp-2">→ {m.response}</p>}
      {m.outcome === "waiting" && (m.rationale || m.reason) && (
        <p className="mt-1 font-sans text-2xs text-state-escalated-text line-clamp-2">
          {m.rationale ?? REASON_LABEL[m.reason ?? "other"]}
        </p>
      )}
    </div>
  );
  return (
    <li className="border-b border-subtle last:border-b-0">
      {m.conversationId ? (
        <Link to="/c/$clientSlug/inbox/$conversationId" params={{ clientSlug, conversationId: m.conversationId }} className="block">
          {inner}
        </Link>
      ) : (
        inner
      )}
    </li>
  );
}

function Daily({ daily }: { daily: Array<StatsCounts & { date: string }> }) {
  const max = Math.max(1, ...daily.map((d) => d.received));
  const h = (n: number) => `${(n * 100) / max}%`;
  return (
    <>
      <div className="flex items-stretch gap-[3px] h-36">
        {daily.map((d) => (
          <div
            key={d.date}
            className="flex-1 min-w-0 flex flex-col justify-end rounded-t-sm overflow-hidden hover:opacity-80"
            title={`${d.date}: ${d.received} recibidos · ${answered(d)} contestados · ${d.waiting} escalados · ${d.notNeeded} sin respuesta necesaria`}
          >
            <span style={{ height: h(d.notNeeded), background: COLOR.notNeeded }} />
            <span style={{ height: h(d.waiting), background: COLOR.waiting }} />
            <span style={{ height: h(answered(d)), background: COLOR.answered }} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between font-mono text-2xs text-text-muted">
        <span>{daily[0]?.date}</span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
      <div className="mt-2 flex gap-4 flex-wrap font-sans text-xs text-text-muted">
        {[
          ["Contestados", COLOR.answered],
          ["Escalados", COLOR.waiting],
          ["No hacía falta", COLOR.notNeeded],
        ].map(([l, c]) => (
          <span key={l} className="inline-flex items-center gap-1.5">
            <i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: c }} />
            {l}
          </span>
        ))}
      </div>
    </>
  );
}

function PostRow({ p, clientSlug }: { p: PostStat; clientSlug: string }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b border-subtle last:border-b-0">
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 hover:bg-surface-2 transition-colors flex gap-3">
        {p.imageUrl ? (
          <img src={p.imageUrl} alt="" className="w-14 h-14 rounded-md object-cover flex-shrink-0 bg-surface-2" loading="lazy" />
        ) : (
          <div className="w-14 h-14 rounded-md bg-surface-2 flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-wrap font-sans text-2xs">
              <span className="text-text-muted">{p.platform}</span>
              {p.theme && <span className="rounded-full bg-state-bot-bg text-state-bot-text px-2 py-0.5">{p.theme}</span>}
              {p.brands.map((b) => (
                <span key={b} className="rounded-full bg-surface-2 border border-default text-text-secondary px-2 py-0.5">
                  {b}
                </span>
              ))}
            </div>
            <span className="font-sans text-lg font-semibold tabular-nums text-text-primary">{p.received}</span>
          </div>
          <p className="mt-1 font-sans text-sm text-text-secondary line-clamp-2">{p.caption ?? "(sin texto)"}</p>
          <div className="mt-1.5">
            <Split c={p} />
          </div>
          <div className="mt-1 font-sans text-2xs text-text-muted">
            {answered(p)} contestados · {p.waiting} escalados
            {p.reasons.length > 0 && ` (${p.reasons.map((r) => `${REASON_LABEL[r.key] ?? r.key} ${r.count}`).join(", ")})`}
          </div>
        </div>
      </button>
      {open && (
        <div className="bg-surface-2/50 border-t border-subtle">
          {p.permalink && (
            <a href={p.permalink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-4 pt-2 font-sans text-xs text-text-secondary hover:text-text-primary">
              Ver publicación <ExternalLink size={12} />
            </a>
          )}
          <ul>
            {p.messages.map((m) => (
              <MessageRow key={m.id} m={m} clientSlug={clientSlug} />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

export function MonitoringStats({ clientSlug, days }: { clientSlug: string; days: number }) {
  const { data } = useModerationStats(clientSlug, days);
  const { data: posts, isLoading: postsLoading } = usePostStats(clientSlug, days);
  const [reason, setReason] = useState<string | null>(null);
  const [theme, setTheme] = useState<string | null>(null);
  const [brand, setBrand] = useState<string | null>(null);

  const waitingShown = useMemo(
    () => (data?.waiting ?? []).filter((m) => !reason || m.reason === reason),
    [data, reason],
  );
  const postsShown = useMemo(
    () =>
      (posts?.posts ?? []).filter(
        (p) => (!theme || (p.theme ?? "Sin clasificar") === theme) && (!brand || p.brands.includes(brand)),
      ),
    [posts, theme, brand],
  );

  if (!data) return null;
  const t = data.totals;

  return (
    <div className="space-y-5">
      <Card
        title="Día por día"
        subtitle={`${t.received} recibidos · ${answered(t)} contestados · ${t.waiting} escalados · respuesta del bot ${
          data.botResponseMinutes === null ? "—" : data.botResponseMinutes < 1 ? "< 1 min" : `${Math.round(data.botResponseMinutes)} min`
        } · ${data.mentions} menciones y compartidos sin texto`}
      >
        <Daily daily={data.daily} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Por red y por tipo">
          <div className="space-y-3">
            {[...Object.entries(data.byPlatform), ...Object.entries(data.byKind)].map(([k, c]) => (
              <div key={k}>
                <div className="flex justify-between font-sans text-sm text-text-primary mb-1">
                  <span>{k === "comment" ? "Comentarios" : k === "dm" ? "Mensajes directos" : k}</span>
                  <span className="tabular-nums font-semibold">{c.received}</span>
                </div>
                <Split c={c} />
                <div className="mt-1 font-sans text-2xs text-text-muted">
                  {answered(c)} contestados · {c.waiting} escalados · {c.notNeeded} no hacía falta
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Por qué se escala" subtitle="Tocá un motivo para ver sus mensajes">
          <div className="space-y-2">
            {data.escalationReasons.length === 0 && <p className="text-sm text-text-muted">Nada escalado en este período.</p>}
            {data.escalationReasons.map((r) => {
              const max = data.escalationReasons[0]?.count ?? 1;
              const active = reason === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setReason(active ? null : r.key)}
                  className={cn(
                    "w-full text-left rounded-lg px-3 py-2 transition-colors",
                    active ? "bg-magenta-soft-bg ring-1 ring-magenta" : "hover:bg-surface-2",
                  )}
                >
                  <div className="flex justify-between font-sans text-sm text-text-primary">
                    <span>{REASON_LABEL[r.key] ?? r.key}</span>
                    <span className="tabular-nums font-semibold">{r.count}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-surface-2 overflow-hidden">
                    <span className="block h-full rounded-full" style={{ width: `${(r.count * 100) / max}%`, background: COLOR.waiting }} />
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      <section className="rounded-xl border border-default bg-surface-1 overflow-hidden">
        <header className="px-5 py-3.5 border-b border-subtle flex items-center justify-between gap-3">
          <div>
            <h2 className="font-sans text-sm font-semibold text-text-primary">
              {reason ? `Escalados: ${REASON_LABEL[reason] ?? reason}` : "Todos los escalados del período"}
              <span className="ml-2 font-mono text-2xs text-text-muted tabular-nums">{waitingShown.length}</span>
            </h2>
            <p className="font-sans text-xs text-text-muted mt-0.5">con el motivo interno del bot</p>
          </div>
          {reason && (
            <button type="button" onClick={() => setReason(null)} className="font-sans text-xs text-text-secondary hover:text-text-primary">
              Ver todos
            </button>
          )}
        </header>
        <ul className="max-h-[420px] overflow-y-auto">
          {waitingShown.map((m) => (
            <MessageRow key={m.id} m={m} clientSlug={clientSlug} />
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-default bg-surface-1 overflow-hidden">
        <header className="px-5 py-3.5 border-b border-subtle space-y-3">
          <div>
            <h2 className="font-sans text-sm font-semibold text-text-primary">Publicaciones con más conversación</h2>
            <p className="font-sans text-xs text-text-muted mt-0.5">
              {postsLoading
                ? "Buscando a qué publicación pertenece cada comentario…"
                : posts
                  ? `${posts.totalPosts} publicaciones con comentarios${posts.commentsWithoutPost ? ` · ${posts.commentsWithoutPost} comentarios sin publicación identificada` : ""}. Tocá una para ver sus comentarios.`
                  : "No se pudo armar el análisis por publicación."}
            </p>
          </div>
          {posts && posts.byTheme.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-sans text-xs text-text-muted mr-1">Tema:</span>
              {posts.byTheme.map((g) => (
                <Chip key={g.name} label={g.name} count={g.received} active={theme === g.name} onClick={() => setTheme(theme === g.name ? null : g.name)} />
              ))}
            </div>
          )}
          {posts && posts.byBrand.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-sans text-xs text-text-muted mr-1">Marca:</span>
              {posts.byBrand.map((g) => (
                <Chip key={g.name} label={g.name} count={g.received} active={brand === g.name} onClick={() => setBrand(brand === g.name ? null : g.name)} />
              ))}
            </div>
          )}
        </header>
        <ul className="max-h-[640px] overflow-y-auto">
          {postsShown.map((p) => (
            <PostRow key={p.id} p={p} clientSlug={clientSlug} />
          ))}
          {posts && postsShown.length === 0 && (
            <li className="px-5 py-8 text-sm text-text-muted text-center">Ninguna publicación con ese filtro.</li>
          )}
        </ul>
      </section>
    </div>
  );
}
